import type { Draft } from 'immer';
import type { AssetMapAndDetailsState } from '~/domain/asset-details-common/store/mapDetails/AssetMapAndDetailsState';
import type { AssetListItem } from '~/data/AssetListItem/AssetListItem';
import type { AssetTagRelationshipsActions } from '~/ducks/asset-tag-relationships/actions';
import AssetTagRelationshipsConstants from '~/ducks/asset-tag-relationships/constants';
import type {
  RelationshipTag,
  RelationshipTagCategory,
} from '~/ducks/asset-tag-relationships/types';
import type { Tag, TagCategory } from '~/ducks/asset-tags/data/Tag';

export const convertToRelationshipTag = (tag: Tag): RelationshipTag => ({
  tag: tag.name,
  tagId: tag.id,
  temporary: true,
});

export const convertToRelationshipTagCategory = (
  category: TagCategory,
  tag: Tag
): RelationshipTagCategory => ({
  categoryId: category.id,
  category: category.name,
  tags: [convertToRelationshipTag(tag)],
});

export const findCategoryIndex = (tags: RelationshipTagCategory[], tagCategory: TagCategory) =>
  tags.findIndex((category) => category.categoryId === tagCategory.id);

export const findTagIndex = (
  tags: RelationshipTagCategory[],
  categoryIndex: number,
  tag: Tag,
  onlyTemporary?: boolean
): number => {
  const tagIndex = tags[categoryIndex]?.tags.findIndex((t) => t.tagId === tag.id) ?? -1;

  if (onlyTemporary) {
    return tags[categoryIndex]?.tags[tagIndex]?.temporary ? tagIndex : -1;
  }

  return tagIndex;
};

export const addTagToAsset = (asset: AssetListItem, tagCategory: TagCategory, tag: Tag): void => {
  const categoryIndex = findCategoryIndex(asset.tags, tagCategory);

  if (categoryIndex < 0) {
    asset.tags = [...asset.tags, convertToRelationshipTagCategory(tagCategory, tag)];
  } else {
    if (findTagIndex(asset.tags, categoryIndex, tag) >= 0) {
      return;
    }
    asset.tags[categoryIndex].tags.push(convertToRelationshipTag(tag));
  }
};

export const removeTagFromAsset = (
  asset: AssetListItem,
  tagCategory: TagCategory,
  tag: Tag,
  onlyTemporary?
): void => {
  const categoryIndex = findCategoryIndex(asset.tags, tagCategory);
  const tagIndex = findTagIndex(asset.tags, categoryIndex, tag, onlyTemporary);

  if (tagIndex >= 0) {
    const numberOfTags = asset.tags[categoryIndex]?.tags.length;

    if (numberOfTags && numberOfTags < 2) {
      asset.tags.splice(categoryIndex, 1);
    } else {
      asset.tags[categoryIndex].tags.splice(tagIndex, 1);
    }
  }
};

export const confirmTag = (asset: AssetListItem, tagCategory: TagCategory, tag: Tag) => {
  const categoryIndex = findCategoryIndex(asset.tags, tagCategory);
  const tagIndex = findTagIndex(asset.tags, categoryIndex, tag);
  if (tagIndex >= 0) {
    asset.tags[categoryIndex].tags[tagIndex].temporary = false;
  }
};

export const handleTagUpdatesInDetails = (
  state: Draft<AssetMapAndDetailsState>,
  action: AssetTagRelationshipsActions
) => {
  switch (action.type) {
    case AssetTagRelationshipsConstants.REMOVE_ASSET_TAG_RELATIONSHIPS_REQUEST_ERROR:
    case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST: {
      const { tagCategory, tag, assets } = action.payload;

      if (state.asset && assets.includes(state.asset.assetDetails.id)) {
        addTagToAsset(state.asset, tagCategory, tag);
      }
      break;
    }

    case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST_SUCCESS: {
      const { tagCategory, tag, assets } = action.payload;

      if (state.asset && assets.includes(state.asset.assetDetails.id)) {
        confirmTag(state.asset, tagCategory, tag);
      }
      break;
    }

    case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST_ERROR: {
      const { tagCategory, tag, assets } = action.payload;

      if (state.asset && assets.includes(state.asset.assetDetails.id)) {
        removeTagFromAsset(state.asset, tagCategory, tag, true);
      }
      break;
    }

    case AssetTagRelationshipsConstants.REMOVE_ASSET_TAG_RELATIONSHIPS_REQUEST: {
      const { tagCategory, tag, assets } = action.payload;

      if (state.asset && assets.includes(state.asset.assetDetails.id)) {
        removeTagFromAsset(state.asset, tagCategory, tag);
      }
      break;
    }
  }
};
